import { Box, Button, Center, useToast } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import useFormError from 'hooks/shared/useFormError';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';

import GridHeading from 'presentation/components/shared/grid/GridHeading';
import AppCard from 'presentation/components/shared/card/AppCard';
import { useSendGeneralSurveyMutation } from 'rtkQuery/sharespro/queries/surveys';
import FormSliderInput from 'presentation/components/shared/form/FormSliderInput';
import { SliderInputMark } from 'presentation/components/shared/fields/SliderInput';
import FormTextInput from 'presentation/components/shared/form/FormTextInput';
import PageLayout from 'presentation/layout/shared/page/PageLayout';

interface FormValues {
  service_quality: number;
  recommend: number;
  offers: number;
  comment: string;
}

const GeneralSurveyPage: FC = () => {
  const { t } = useTranslation();

  const [sendSurvey, { isLoading }] = useSendGeneralSurveyMutation();

  const formErrorHandler = useFormError<FormValues>();

  const toast = useToast();

  const navigate = useNavigate();

  const marks: SliderInputMark[] = [];
  for (let i = 1; i <= 10; i++) {
    marks.push({
      label: i.toString(),
      value: i,
    });
  }

  return (
    <PageLayout>
      <Helmet>
        <title>{t('survey')}</title>
      </Helmet>
      <Box w="720px" margin="0 auto">
        <GridHeading>{t('survey')}</GridHeading>
        <AppCard p="40px" display="flex" flexDirection="column">
          <Formik<FormValues>
            initialValues={{
              service_quality: 5,
              recommend: 5,
              offers: 5,
              comment: '',
            }}
            onSubmit={(values, formik) => {
              sendSurvey({
                service_quality: values.service_quality,
                recommend: values.recommend,
                offers: values.offers,
                comment: values.comment,
              })
                .unwrap()
                .then(() => {
                  toast({
                    description: t('thanksForSurveyFeedback'),
                    status: 'success',
                    position: 'top-right',
                    isClosable: true,
                  });
                  navigate(AppRoute.home);
                })
                .catch((err) => formErrorHandler(err, formik));
            }}
          >
            {({ values, handleChange, setFieldValue }) => {
              return (
                <Form>
                  <Box mb="60px">
                    <FormSliderInput
                      name="service_quality"
                      min={1}
                      max={10}
                      step={1}
                      marks={marks}
                      subLabel={t('generalSurveyQuestion1')}
                      onChange={(val) => {
                        setFieldValue('service_quality', val);
                      }}
                      value={values.service_quality}
                    />
                  </Box>
                  <Box mb="60px">
                    <FormSliderInput
                      name="recommend"
                      min={1}
                      max={10}
                      step={1}
                      marks={marks}
                      subLabel={t('generalSurveyQuestion2')}
                      onChange={(val) => {
                        setFieldValue('recommend', val);
                      }}
                      value={values.recommend}
                    />
                  </Box>
                  <Box mb="50px">
                    <FormSliderInput
                      name="offers"
                      min={1}
                      max={10}
                      step={1}
                      marks={marks}
                      subLabel={t('generalSurveyQuestion3')}
                      onChange={(val) => {
                        setFieldValue('offers', val);
                      }}
                      value={values.offers}
                    />
                  </Box>
                  {(values.offers < 10 ||
                    values.service_quality < 10 ||
                    values.recommend < 10) && (
                    <Box mb="40px">
                      <FormTextInput
                        name="comment"
                        onChange={handleChange}
                        value={values.comment}
                        subLabel={t('generalSurveyQuestion4')}
                      />
                    </Box>
                  )}
                  <Center>
                    <Button type="submit" isLoading={isLoading}>
                      {t('finishSurvey')}
                    </Button>
                  </Center>
                </Form>
              );
            }}
          </Formik>
        </AppCard>
      </Box>
    </PageLayout>
  );
};

export default GeneralSurveyPage;
